import React from "react";
import {
  ComposerPrimitive,
  useComposer,
  useComposerRuntime,
} from "@assistant-ui/react";
import classes from "./Chat.module.css";

const moveCaretToEnd = (element: HTMLTextAreaElement | null) => {
  if (!element) {
    return;
  }

  requestAnimationFrame(() => {
    const nextCursorPosition = element.value.length;
    element.setSelectionRange(nextCursorPosition, nextCursorPosition);
  });
};

const ChatComposer = () => {
  const composerRuntime = useComposerRuntime();
  const composerText = useComposer((state) => state.text);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const submittedPromptsRef = React.useRef<string[]>([]);
  const historyIndexRef = React.useRef<number | null>(null);
  const draftBeforeHistoryRef = React.useRef("");

  const handleSubmit = React.useCallback(() => {
    const trimmedText = composerText.trim();
    if (!trimmedText) {
      return;
    }

    submittedPromptsRef.current = [
      ...submittedPromptsRef.current,
      trimmedText,
    ];
    historyIndexRef.current = null;
    draftBeforeHistoryRef.current = "";
  }, [composerText]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (historyIndexRef.current === null) {
        return;
      }

      historyIndexRef.current = null;
      draftBeforeHistoryRef.current = event.target.value;
    },
    []
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = event.currentTarget;
      const selectionStart = textarea.selectionStart ?? 0;
      const selectionEnd = textarea.selectionEnd ?? 0;
      const isAtStart = selectionStart === 0 && selectionEnd === 0;
      const isAtEnd =
        selectionStart === textarea.value.length &&
        selectionEnd === textarea.value.length;

      if (event.key === "ArrowUp") {
        if (!isAtStart || submittedPromptsRef.current.length === 0) {
          return;
        }

        event.preventDefault();

        if (historyIndexRef.current === null) {
          draftBeforeHistoryRef.current = composerText;
          historyIndexRef.current = submittedPromptsRef.current.length - 1;
        } else if (historyIndexRef.current > 0) {
          historyIndexRef.current -= 1;
        }

        composerRuntime.setText(
          submittedPromptsRef.current[historyIndexRef.current]
        );
        moveCaretToEnd(inputRef.current);
        return;
      }

      if (event.key === "ArrowDown") {
        if (!isAtEnd || historyIndexRef.current === null) {
          return;
        }

        event.preventDefault();

        if (historyIndexRef.current < submittedPromptsRef.current.length - 1) {
          historyIndexRef.current += 1;
          composerRuntime.setText(
            submittedPromptsRef.current[historyIndexRef.current]
          );
        } else {
          historyIndexRef.current = null;
          composerRuntime.setText(draftBeforeHistoryRef.current);
          draftBeforeHistoryRef.current = "";
        }

        moveCaretToEnd(inputRef.current);
      }
    },
    [composerRuntime, composerText]
  );

  return (
    <ComposerPrimitive.Root
      className={classes.composerForm}
      onSubmit={handleSubmit}
    >
      <ComposerPrimitive.Input
        ref={inputRef}
        className={classes.composerInput}
        placeholder="Type a message..."
        submitMode="enter"
        rows={2}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      <ComposerPrimitive.Send className={classes.sendButton}>
        Send
      </ComposerPrimitive.Send>
    </ComposerPrimitive.Root>
  );
};

export default ChatComposer;
